import React, { useEffect, useState, useCallback, useRef } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Skeleton } from 'primereact/skeleton';
import { Tag } from 'primereact/tag';
import { OverlayPanel } from 'primereact/overlaypanel';
import { PushLogService } from '../../service/PushLogService';
import moment from 'moment';

const pushLogService = new PushLogService();

const RISK_LEVEL_LABEL = {
    churn_risk_early: 'Inicial',
    churn_risk_medium: 'Médio',
    churn_risk_critical: 'Crítico',
};

const RISK_LEVEL_SEVERITY = {
    churn_risk_early: 'info',
    churn_risk_medium: 'warning',
    churn_risk_critical: 'danger',
};

const riskLevelOptions = Object.entries(RISK_LEVEL_LABEL).map(([value, label]) => ({ label, value }));

const openedOptions = [
    { label: 'Abriu', value: 'true' },
    { label: 'Não abriu', value: 'false' },
];

const formatDateTime = (value) => (value ? moment(value).locale('pt-br').format('DD/MM/YYYY HH:mm') : '—');

const userBody = (rowData) => (
    <div>
        <div>{rowData.userName || '—'}</div>
        {rowData.userEmail && <div className="text-sm text-color-secondary">{rowData.userEmail}</div>}
    </div>
);

const riskLevelBody = (rowData) => (
    <Tag value={RISK_LEVEL_LABEL[rowData.templateKey] || rowData.templateKey} severity={RISK_LEVEL_SEVERITY[rowData.templateKey]} />
);

const sentAtBody = (rowData) => formatDateTime(rowData.sentAt);

const openedBody = (rowData) => (
    <div>
        <Tag value={rowData.opened ? 'Sim' : 'Não'} severity={rowData.opened ? 'success' : null} />
        {rowData.opened && (
            <div className="text-sm text-color-secondary mt-1">{formatDateTime(rowData.openedAt)}</div>
        )}
    </div>
);

const defaultGridParams = { offset: 0, limit: 20 };

// Lista as notificações de risco de churn enviadas (PushLog, source='churn_risk')
// com o status de abertura reportado pelo app — ver POST
// /push-logs/{notificationId}/opened. E-mail de churn não tem log nenhum hoje
// (só push), então essa tela cobre só push.
export const ChurnNotificationsView = () => {
    const [data, setData] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [gridParams, setGridParams] = useState(defaultGridParams);
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const [templateKey, setTemplateKey] = useState(null);
    const [opened, setOpened] = useState(null);
    const [selectedError, setSelectedError] = useState('');
    const errorPanelRef = useRef(null);

    // Erro só aparece ao clicar na tag "Falhou" (OverlayPanel), em vez de
    // sempre exibido embaixo — evita alargar/aumentar a linha da grid pra
    // todo mundo por causa de mensagens de erro longas.
    const onDeliveryTagClick = (event, rowData) => {
        if (rowData.success || !rowData.errorReason) return;
        setSelectedError(rowData.errorReason);
        errorPanelRef.current?.toggle(event);
    };

    const deliveryBody = (rowData) => (
        <Tag
            value={rowData.success ? 'Enviado' : 'Falhou'}
            severity={rowData.success ? 'success' : 'danger'}
            className={!rowData.success && rowData.errorReason ? 'cursor-pointer' : undefined}
            onClick={(e) => onDeliveryTagClick(e, rowData)}
        />
    );

    const fetchGrid = useCallback(async (params) => {
        setLoading(true);
        setError(null);
        try {
            const page = await pushLogService.getChurnNotifications({
                offset: params.offset,
                limit: params.limit,
                search: params.search,
                templateKey: params.templateKey,
                opened: params.opened,
            });
            setData(page.data || []);
            setTotalRecords(page.totalRecords || 0);
        } catch (e) {
            setError('Não foi possível carregar as notificações de churn.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchGrid({ ...gridParams, search, templateKey, opened });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [gridParams, search, templateKey, opened]);

    // Debounce: só busca 500ms depois que o usuário parar de digitar (mesmo
    // padrão de StudentsRankingTable.js).
    useEffect(() => {
        const handle = setTimeout(() => {
            if (searchInput !== search) {
                setSearch(searchInput);
                setGridParams((prev) => ({ ...prev, offset: 0 }));
            }
        }, 500);
        return () => clearTimeout(handle);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchInput]);

    const reload = () => fetchGrid({ ...gridParams, search, templateKey, opened });

    const filterStartTemplate = () => (
        <div className="flex flex-column gap-1">
            <label htmlFor="churn-notif-level" className="text-xs text-color-secondary">Nível de risco</label>
            <Dropdown
                inputId="churn-notif-level"
                value={templateKey}
                options={riskLevelOptions}
                onChange={(e) => { setTemplateKey(e.value); setGridParams((prev) => ({ ...prev, offset: 0 })); }}
                placeholder="Todos os níveis"
                showClear
                disabled={loading}
                className="w-full md:w-15rem"
            />
        </div>
    );

    const filterOpenedTemplate = () => (
        <div className="flex flex-column gap-1">
            <label htmlFor="churn-notif-opened" className="text-xs text-color-secondary">Abertura</label>
            <Dropdown
                inputId="churn-notif-opened"
                value={opened}
                options={openedOptions}
                onChange={(e) => { setOpened(e.value); setGridParams((prev) => ({ ...prev, offset: 0 })); }}
                placeholder="Todos"
                showClear
                disabled={loading}
                className="w-full md:w-15rem"
            />
        </div>
    );

    // Botão ao lado do input — busca na hora, sem esperar os 500ms do
    // debounce (mesmo padrão de StudentsRankingTable.js/UsersView.js).
    const onSearchClick = () => {
        setSearch(searchInput);
        setGridParams((prev) => ({ ...prev, offset: 0 }));
    };

    const filterEndTemplate = () => (
        <div className="flex flex-column gap-1">
            <label htmlFor="churn-notif-search" className="text-xs text-color-secondary">Buscar usuário</label>
            <div className="flex gap-2">
                <InputText
                    id="churn-notif-search"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    placeholder="Nome ou email"
                    disabled={loading}
                    className="w-full md:w-20rem"
                />
                <Button icon="pi pi-search" loading={loading} onClick={onSearchClick} />
            </div>
        </div>
    );

    const gridHeaderTemplate = () => (
        <div className="flex flex-column md:flex-row md:justify-content-between md:align-items-center gap-3">
            <div className="flex flex-wrap gap-3">
                {filterStartTemplate()}
                {filterOpenedTemplate()}
            </div>
            {filterEndTemplate()}
        </div>
    );

    return (
        <div className="grid crud-demo">
            <div className="col-12">
                <div className="card">
                    <div className="flex justify-content-between align-items-center mb-3">
                        <h5 className="m-0">Notificações de churn</h5>
                        <Button
                            icon="pi pi-refresh"
                            rounded
                            text
                            onClick={reload}
                            disabled={loading}
                        />
                    </div>
                    {error ? (
                        <div className="flex flex-column align-items-center justify-content-center p-5">
                            <i className="pi pi-exclamation-triangle mb-3" style={{ fontSize: '2rem', color: 'var(--red-500)' }}></i>
                            <span className="text-lg">{error}</span>
                            <Button label="Tentar Novamente" className="p-button-text mt-3" onClick={reload} />
                        </div>
                    ) : (
                        <DataTable
                            value={loading ? Array.from({ length: gridParams.limit }, (_, i) => ({ id: `skeleton-${i}` })) : data}
                            header={gridHeaderTemplate()}
                            dataKey="id"
                            lazy
                            paginator
                            first={gridParams.offset}
                            rows={gridParams.limit}
                            totalRecords={totalRecords}
                            rowsPerPageOptions={[20, 50, 100]}
                            onPage={(e) => setGridParams({ offset: e.first, limit: e.rows })}
                            responsiveLayout="scroll"
                            emptyMessage="Nenhuma notificação de churn encontrada nesse filtro."
                        >
                            <Column field="userName" header="Usuário" body={loading ? () => <Skeleton /> : userBody} />
                            <Column field="templateKey" header="Nível" body={loading ? () => <Skeleton /> : riskLevelBody} style={{ width: '10rem' }} />
                            <Column field="sentAt" header="Enviado em" body={loading ? () => <Skeleton /> : sentAtBody} style={{ width: '12rem' }} />
                            <Column field="success" header="Envio" body={loading ? () => <Skeleton /> : deliveryBody} style={{ width: '10rem' }} />
                            <Column field="opened" header="Aberto" body={loading ? () => <Skeleton /> : openedBody} style={{ width: '12rem' }} />
                        </DataTable>
                    )}
                </div>
            </div>

            <OverlayPanel ref={errorPanelRef}>
                <div style={{ maxWidth: '24rem' }}>{selectedError}</div>
            </OverlayPanel>
        </div>
    );
};

export default ChurnNotificationsView;
